

import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { useDispatch } from 'react-redux';

import { getAdminProductModelData } from '../../actions/officeAction';
import Loader from '../../components/Loader';
import PaginationComponent from '../../components/PaginationComponent';
import AdminSearchFilterPanel from '../../components/AdminSearchFilterPanel/AdminSearchFilterPanel';
import { resolveAnyStaticUrl } from '../../urlConfig';
import {
    analyzeCatalogProductLinks,
    applyCatalogProductLinks,
    revertCatalogProductLinks,
    downloadCatalogExtensionReport,
    estimateCatalogExtension,
    listPricingProducts,
    saveCatalogExtensionGeometry,
} from './catalogReviewApi';
import {
    disposeCatalogModelData,
    measureCatalogModelGeometry,
} from './catalogGeometryMeasurement';

const CATALOG_FROM = 1;
const CATALOG_TO = 1150;
const ALL_CATEGORIES_ID = '__all__';
const PRICING_FILTERS = new Set([
    'all',
    'unchecked',
    'checked',
    'notImported',
    'imported',
]);


const SECTIONS = [
    { value: 'catalog', label: 'Каталог и товары' },
    { value: 'geometry', label: '3D и размеры' },
    { value: 'pricing', label: 'Цены и подтверждение' },
    { value: 'issues', label: 'Проблемы и отчёт' },
];

const money = (value) => {
    const number = Number(value);
    return Number.isFinite(number) && number > 0
        ? `${number.toLocaleString('ru-RU')} ₽`
        : '—';
};

const size = (dimensions = {}) => {
    const height = Number(dimensions?.height || 0);
    const width = Number(dimensions?.width || 0);
    const thickness = Number(dimensions?.thickness || 0);

    return height > 0 && width > 0 && thickness > 0
        ? `${Math.round(height)}×${Math.round(width)}×${Math.round(thickness)} мм`
        : '—';
};

const hasDimensions = (dimensions = {}) => (
    Number(dimensions?.height || 0) > 0
    && Number(dimensions?.width || 0) > 0
    && Number(dimensions?.thickness || 0) > 0
);

const getErrorMessage = (error) => (
    error?.response?.data?.message
    || error?.message
    || 'Произошла ошибка'
);

const productName = (product) => {
    const safeProduct = product && typeof product === 'object' ? product : {};

    return (
        safeProduct.fullName
        || safeProduct.name
        || `Товар ${safeProduct.article || ''}`.trim()
        || 'Товар не найден'
    );
};

const getErrorCode = (error) => (
    error?.response?.data?.code
    || error?.code
    || ''
);

const isReviewedStatus = (status) => (
    ['approved', 'edited', 'manual', 'source-confirmed'].includes(String(status || ''))
);

const geometryStatusLabel = (status) => {
    if (status === 'measured') return 'Размер получен';
    if (status === 'pending') return 'Ожидает анализа';
    if (status === 'failed') return 'Ошибка анализа';
    if (status === 'unavailable') return 'Нет 3D';
    return 'Не проверено';
};

const reviewStatusLabel = (status) => {
    if (status === 'approved') return 'Подтверждено';
    if (status === 'edited') return 'Исправлено';
    if (status === 'manual') return 'Ручная проверка';
    if (status === 'source-confirmed') return 'Из ведомости';
    return 'Не проверено';
};


const formatPriceEditTime = (value) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';

    return date.toLocaleString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
    });
};

const getLastPriceEditFromItem = (item = {}) => {
    if (item?.lastPriceEdit?.userName) return item.lastPriceEdit;

    const logs = Array.isArray(item?.auditLog) ? item.auditLog : [];
    const entry = [...logs].reverse().find((row) => row?.action === 'pricing-update');
    if (entry?.userName) {
        return {
            userId: entry.userId || null,
            userName: entry.userName,
            createdAt: entry.createdAt || null,
        };
    }

    if (item?.updatedByName && item?.reviewedAt) {
        return {
            userId: item.updatedBy || null,
            userName: item.updatedByName,
            createdAt: item.reviewedAt,
        };
    }

    return null;
};

const rowStateLabel = (row = {}) => {
    if (row.status === 'duplicate') return 'Дубль номера';
    if (row.status === 'ready') return 'Нужно синхронизировать';
    if (row.status === 'orphan-item') return 'Товар не найден';
    if (row.transfer?.status === 'imported') return 'Перенесено в товар';
    if (row.transfer?.status === 'changed') return 'Изменено после переноса';
    if (row.transfer?.status === 'pending') return 'Готово к переносу';
    if (row.existingItem?.catalogSource?.type === 'new-catalog-product') {
        return 'Новый каталог';
    }
    if (row.existingItem) return 'Связано';
    return 'Не обработано';
};

const CatalogLoadingState = ({
    title,
    description = '',
    compact = false,
}) => (
    <div
        className={`catalogReview__extensionLoadingState ${compact ? 'is-compact' : ''}`}
        role="status"
        aria-live="polite"
    >
        <Loader typeSize={compact ? '42px' : '68px'} />
        <div>
            <strong>{title}</strong>
            {description ? <span>{description}</span> : null}
        </div>
    </div>
);

const GalleryModal = ({ row, activeIndex: sharedActiveIndex = 0, onActiveIndexChange, onClose }) => {
    const images = Array.from(new Set([
        row?.imagePath,
        ...(row?.galleryImages || []),
        ...(row?.product?.galleryImages || []),
    ].filter(Boolean)));
    const [activeIndex, setActiveIndex] = useState(
        Math.max(0, Math.min(images.length - 1, Number(sharedActiveIndex) || 0))
    );

    useEffect(() => {
        setActiveIndex(
            Math.max(0, Math.min(images.length - 1, Number(sharedActiveIndex) || 0))
        );
    }, [images.length, sharedActiveIndex]);

    if (!row || !images.length) return null;

    return (
        <div
            className="catalogReview__extensionGalleryBackdrop"
            onMouseDown={onClose}
            role="presentation"
        >
            <div
                className="catalogReview__extensionGallery"
                data-catalog-sync-scope={`gallery-${row._scopeKey || row.purchasedNumber}`}
                onMouseDown={(event) => event.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-label={`Фотографии формы №${row.purchasedNumber}`}
            >
                <header>
                    <div>
                        <strong>Форма №{row.purchasedNumber}</strong>
                        <span>{productName(row.product)}</span>
                    </div>
                    <button type="button" onClick={onClose}>Закрыть</button>
                </header>

                <div
                    className="catalogReview__extensionGalleryMain"
                    data-catalog-scroll-id={`gallery-main-${row._scopeKey || row.purchasedNumber}`}
                >
                    <img
                        src={resolveAnyStaticUrl(images[activeIndex])}
                        alt={`Форма №${row.purchasedNumber}, вид ${activeIndex + 1}`}
                    />
                </div>

                <div
                    className="catalogReview__extensionGalleryThumbs"
                    data-catalog-scroll-id={`gallery-thumbs-${row._scopeKey || row.purchasedNumber}`}
                >
                    {images.map((image, index) => (
                        <button
                            type="button"
                            key={`${image}-${index}`}
                            className={activeIndex === index ? 'is-active' : ''}
                            onClick={() => { setActiveIndex(index); onActiveIndexChange?.(index); }}
                        >
                            <img
                                src={resolveAnyStaticUrl(image)}
                                alt={`Вид ${index + 1}`}
                            />
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
};

const CatalogExtensionPanel = ({
    categories = [],
    categoryId = '',
    onRefresh,
    refreshToken = 0,
    sharedDraft = null,
    onDraftChange,
    onOpenPricing,
    onRealtimeChange,
    realtimeOperations = {},
    onRealtimeOperation,
}) => {
    const dispatch = useDispatch();
    const [workspace, setWorkspace] = useState(null);
    const [activeSection, setActiveSection] = useState('catalog');
    const [geometryFilter, setGeometryFilter] = useState('unchecked');
    const [pricingFilter, setPricingFilter] = useState('unchecked');
    const [catalogSearchDraft, setCatalogSearchDraft] = useState('');
    const [catalogSearch, setCatalogSearch] = useState('');
    const [catalogSearchPanelOpen, setCatalogSearchPanelOpen] = useState(false);
    const [pageNumber, setPageNumber] = useState(0);
    const pageSize = 30;
    const [workspaceLoading, setWorkspaceLoading] = useState(Boolean(categoryId));
    const [actionLoading, setActionLoading] = useState('');
    const [singleTransferNumber, setSingleTransferNumber] = useState(null);
    const [singleRevertNumber, setSingleRevertNumber] = useState(null);
    const [message, setMessage] = useState(null);
    const [galleryRow, setGalleryRow] = useState(null);
    const [categoryProgress, setCategoryProgress] = useState({
        current: 0,
        total: 0,
        name: '',
    });
    const [batch, setBatch] = useState({
        running: false,
        current: 0,
        total: 0,
        success: 0,
        failed: 0,
        currentNumber: null,
    });

    const cancelBatchRef = useRef(false);
    const handledRefreshTokenRef = useRef(0);
    const lastBatchOperationAtRef = useRef(0);
    const workspaceRequestIdRef = useRef(0);
    const catalogSearchLocalEditRef = useRef(false);
    const catalogSearchSharedValueRef = useRef(null);

    const isAllCategories = categoryId === ALL_CATEGORIES_ID;

    const selectedCategories = useMemo(() => (
        isAllCategories
            ? categories
            : categories.filter(
                (category) => String(category._id) === String(categoryId)
            )
    ), [categories, categoryId, isAllCategories]);

    const payload = useMemo(() => ({
        categoryId: isAllCategories ? '' : categoryId,
        fromNumber: CATALOG_FROM,
        toNumber: CATALOG_TO,
    }), [categoryId, isAllCategories]);

    const operationKey = useCallback((name) => (
        `extension:${name}:${categoryId || 'none'}`
    ), [categoryId]);

    const remoteAnalyze = realtimeOperations[operationKey('analyze')] || null;
    const remoteGeometry = realtimeOperations[operationKey('geometry')] || null;
    const remoteEstimate = realtimeOperations[operationKey('estimate')] || null;
    const remoteApply = realtimeOperations[operationKey('apply')] || null;
    const remoteReport = realtimeOperations[operationKey('report')] || null;
    const remoteBusy = Boolean(
        remoteAnalyze || remoteGeometry || remoteEstimate || remoteApply || remoteReport
    );
    const singleTransferRunning = singleTransferNumber !== null;
    const singleRevertRunning = singleRevertNumber !== null;
    const effectiveLoading = workspaceLoading
        || Boolean(actionLoading)
        || singleTransferRunning
        || singleRevertRunning
        || remoteBusy;
    const analyzeRunning = actionLoading === 'analyze' || Boolean(remoteAnalyze);
    const estimateRunning = actionLoading === 'estimate' || Boolean(remoteEstimate);
    const applyRunning = ['prepare-apply', 'apply'].includes(actionLoading) || Boolean(remoteApply);
    const reportRunning = actionLoading === 'report' || Boolean(remoteReport);
    const reviewQueueLoading = actionLoading === 'prepare-review';
    const geometryQueueLoading = actionLoading === 'prepare-geometry';
    const effectiveCategoryProgress = categoryProgress.total > 0
        ? categoryProgress
        : {
            current: Number(remoteAnalyze?.current || 0),
            total: Number(remoteAnalyze?.total || 0),
            name: String(remoteAnalyze?.categoryName || ''),
        };
    const effectiveBatch = batch.running || !remoteGeometry
        ? batch
        : {
            running: true,
            current: Number(remoteGeometry.current || 0),
            total: Number(remoteGeometry.total || 0),
            success: Number(remoteGeometry.success || 0),
            failed: Number(remoteGeometry.failed || 0),
            currentNumber: remoteGeometry.currentNumber || null,
        };

    useEffect(() => {
        const section = sharedDraft?.catalogWorkspaceTab;
        if (SECTIONS.some((row) => row.value === section)) {
            setActiveSection(section);
        }

        const nextGeometryFilter = sharedDraft?.catalogGeometryFilter;
        if (['all', 'unchecked', 'measured', 'failed', 'without3D'].includes(nextGeometryFilter)) {
            setGeometryFilter(nextGeometryFilter);
        }

        const nextPricingFilter = sharedDraft?.catalogPricingFilter;
        if (PRICING_FILTERS.has(String(nextPricingFilter || ''))) {
            setPricingFilter(nextPricingFilter);
        }

        if (typeof sharedDraft?.catalogSearchPanelOpen === 'boolean') {
            setCatalogSearchPanelOpen(sharedDraft.catalogSearchPanelOpen);
        }

        if (typeof sharedDraft?.catalogSearch === 'string') {
            const nextSearch = String(sharedDraft.catalogSearch || '');

            /*
             * Hydration из authoritative workspace не является локальным
             * вводом. Иначе debounce отправит remote search обратно в socket
             * и два клиента начнут подтверждать друг друга по кругу.
             */
            if (catalogSearchSharedValueRef.current !== nextSearch) {
                catalogSearchSharedValueRef.current = nextSearch;
                catalogSearchLocalEditRef.current = false;
                setCatalogSearchDraft(nextSearch);
                setCatalogSearch(nextSearch.trim());
            }
        }

        if (Number.isFinite(Number(sharedDraft?.catalogPageNumber))) {
            const nextPage = Math.max(
                0,
                Math.floor(Number(sharedDraft.catalogPageNumber) || 0)
            );

            setPageNumber((previous) => (
                previous === nextPage ? previous : nextPage
            ));
        }
    }, [
        sharedDraft?.catalogWorkspaceTab,
        sharedDraft?.catalogGeometryFilter,
        sharedDraft?.catalogPricingFilter,
        sharedDraft?.catalogSearchPanelOpen,
        sharedDraft?.catalogSearch,
        sharedDraft?.catalogPageNumber,
    ]);

    useEffect(() => {
        const timer = window.setTimeout(() => {
            const nextSearch = String(catalogSearchDraft || '').trim();
            setCatalogSearch(nextSearch);

            if (!catalogSearchLocalEditRef.current) return;

            catalogSearchLocalEditRef.current = false;
            catalogSearchSharedValueRef.current = nextSearch;
            setPageNumber(0);

            onDraftChange?.({
                catalogSearch: nextSearch,
                catalogPageNumber: 0,
            });
        }, 280);

        return () => window.clearTimeout(timer);
    }, [
        catalogSearchDraft,
        onDraftChange,
    ]);

    const changeCatalogSearchDraft = useCallback((value) => {
        catalogSearchLocalEditRef.current = true;
        setCatalogSearchDraft(value);
    }, []);

    const changeCatalogSearchPanelOpen = useCallback((nextOpen) => {
        const normalizedOpen = nextOpen === true;

        setCatalogSearchPanelOpen(normalizedOpen);
        onDraftChange?.({
            catalogSearchPanelOpen: normalizedOpen,
        });
    }, [onDraftChange]);

    const showMessage = useCallback((type, text) => {
        setMessage({ type, text });
    }, []);

    const buildWorkspaceParams = useCallback((overrides = {}) => ({
        mode: 'catalog',
        page: overrides.page ?? pageNumber,
        limit: overrides.limit ?? pageSize,
        categoryId: isAllCategories ? '' : categoryId,
        categoryIds: isAllCategories
            ? selectedCategories.map((category) => category._id).join(',')
            : '',
        section: overrides.section || activeSection,
        geometryFilter: overrides.geometryFilter || geometryFilter,
        pricingFilter: overrides.pricingFilter || pricingFilter,
        search: overrides.search ?? catalogSearch,
        forAction: overrides.forAction === true,
    }), [
        activeSection,
        catalogSearch,
        categoryId,
        geometryFilter,
        isAllCategories,
        pageNumber,
        pageSize,
        pricingFilter,
        selectedCategories,
    ]);

    const loadWorkspace = useCallback(async ({ silent = false } = {}) => {
        if (!categoryId) {
            setWorkspace(null);
            return null;
        }

        const requestId = workspaceRequestIdRef.current + 1;
        workspaceRequestIdRef.current = requestId;
        if (!silent) setWorkspaceLoading(true);

        try {
            const result = await listPricingProducts(buildWorkspaceParams());
            if (workspaceRequestIdRef.current !== requestId) return null;
            setWorkspace(result);
            return result;
        } catch (error) {
            if (workspaceRequestIdRef.current !== requestId) return null;
            if (!silent) showMessage('error', getErrorMessage(error));
            return null;
        } finally {
            /*
             * Важный race-fix: silent refresh тоже может стать последним
             * запросом и тем самым отменить предыдущий обычный запрос.
             * Раньше обычный запрос уже не мог снять workspaceLoading, а
             * silent-запрос принципиально его не снимал — overlay зависал.
             * Последний актуальный запрос всегда обязан закрыть loader.
             */
            if (workspaceRequestIdRef.current === requestId) {
                setWorkspaceLoading(false);
            }
        }
    }, [buildWorkspaceParams, categoryId, showMessage]);

    const loadAllRowsForAction = useCallback(async (overrides = {}) => {
        const collected = [];
        let page = 0;
        let totalPages = 1;

        do {
            const result = await listPricingProducts(buildWorkspaceParams({
                ...overrides,
                page,
                limit: 100,
                forAction: true,
            }));
            collected.push(...(Array.isArray(result?.rows) ? result.rows : []));
            totalPages = Math.max(1, Number(result?.pagination?.totalPages || 1));
            page += 1;
        } while (page < totalPages);

        return collected;
    }, [buildWorkspaceParams]);

    useEffect(() => {
        if (!categoryId) {
            setWorkspace(null);
            setWorkspaceLoading(false);
            return;
        }

        setWorkspaceLoading(true);
        loadWorkspace().catch(() => {});
    }, [categoryId, loadWorkspace]);

    useEffect(() => {
        const totalPages = Math.max(1, Number(workspace?.pagination?.totalPages || 1));
        if (pageNumber > totalPages - 1) {
            setPageNumber(Math.max(0, totalPages - 1));
        }
    }, [pageNumber, workspace?.pagination?.totalPages]);

    useEffect(() => {
        if (
            !refreshToken
            || refreshToken === handledRefreshTokenRef.current
            || effectiveBatch.running
            || !categoryId
        ) {
            return;
        }

        handledRefreshTokenRef.current = refreshToken;
        loadWorkspace().catch(() => {});
    }, [
        refreshToken,
        effectiveBatch.running,
        categoryId,
        loadWorkspace,
    ]);

    const analyzeAll = useCallback(async () => {
        if (!categoryId) {
            showMessage('error', 'Выбери категорию товаров.');
            return;
        }

        const targets = isAllCategories ? selectedCategories : selectedCategories.slice(0, 1);
        if (!targets.length) {
            showMessage('error', 'В выбранном наборе нет категорий.');
            return;
        }

        const realtimeKey = operationKey('analyze');
        setActionLoading('analyze');
        setMessage(null);
        setCategoryProgress({ current: 0, total: targets.length, name: '' });
        onRealtimeOperation?.({ key: realtimeKey, running: true, total: targets.length });

        let success = 0;
        let failed = 0;

        try {
            for (let index = 0; index < targets.length; index += 1) {
                const category = targets[index];
                setCategoryProgress({
                    current: index + 1,
                    total: targets.length,
                    name: category.name || '',
                });
                onRealtimeOperation?.({
                    key: realtimeKey,
                    running: true,
                    current: index + 1,
                    total: targets.length,
                    categoryName: category.name || '',
                });

                try {
                    await analyzeCatalogProductLinks({ categoryId: category._id });
                    success += 1;
                } catch (error) {
                    failed += 1;
                    console.warn(
                        `[CatalogReview][analyze][${category.name || category._id}]`,
                        getErrorMessage(error)
                    );
                }
            }

            const result = await loadWorkspace({ silent: true });
            showMessage(
                failed ? 'warning' : 'success',
                isAllCategories
                    ? `Категории обработаны: ${success}. Ошибок: ${failed}.`
                    : (result?.message || 'Каталог товаров синхронизирован')
            );
            await onRefresh?.(result?.analysis || null);
            await onRealtimeChange?.({
                kind: 'catalog-analysis',
                categoryId: isAllCategories ? '' : categoryId,
                categoryIds: targets.map((category) => category._id),
            });
        } catch (error) {
            showMessage('error', getErrorMessage(error));
        } finally {
            setActionLoading('');
            setCategoryProgress({ current: 0, total: 0, name: '' });
            onRealtimeOperation?.({ key: realtimeKey, running: false });
        }
    }, [
        categoryId,
        isAllCategories,
        loadWorkspace,
        onRealtimeChange,
        onRealtimeOperation,
        onRefresh,
        operationKey,
        selectedCategories,
        showMessage,
    ]);

    const rows = workspace?.rows || [];

    useEffect(() => {
        const scopeKey = String(sharedDraft?.galleryScopeKey || '');
        const purchasedNumber = Number(sharedDraft?.galleryPurchasedNumber || 0);

        if (!scopeKey && !purchasedNumber) {
            setGalleryRow(null);
            return;
        }

        const row = rows.find((candidate) => (
            scopeKey
                ? String(candidate._scopeKey || '') === scopeKey
                : Number(candidate.purchasedNumber) === purchasedNumber
        ));

        setGalleryRow(row || null);
    }, [
        rows,
        sharedDraft?.galleryPurchasedNumber,
        sharedDraft?.galleryScopeKey,
    ]);

    const openGallery = useCallback((row) => {
        if (!row) return;
        setGalleryRow(row);
        onDraftChange?.({
            galleryPurchasedNumber: row.purchasedNumber,
            galleryScopeKey: row._scopeKey || '',
            galleryImageIndex: 0,
        });
    }, [onDraftChange]);

    const closeGallery = useCallback(() => {
        setGalleryRow(null);
        onDraftChange?.({
            galleryPurchasedNumber: null,
            galleryScopeKey: '',
            galleryImageIndex: 0,
        });
    }, [onDraftChange]);

    const catalogRows = useMemo(() => (
        rows
            .filter((row) => row.hasCurrentMatch || row.status === 'duplicate')
            .sort((left, right) => (
                Number(left.purchasedNumber) - Number(right.purchasedNumber)
            ))
    ), [rows]);

    const linkedRows = useMemo(() => (
        catalogRows.filter((row) => (
            row.status !== 'duplicate'
            && row.hasCurrentMatch
        ))
    ), [catalogRows]);

    const geometryRows = useMemo(() => (
        [...linkedRows].sort((left, right) => {
            const rank = (row) => {
                if (row.geometryStatus === 'failed') return 0;
                if (row.geometryStatus === 'pending') return 1;
                if (!row.has3D) return 2;
                if (row.geometryStatus === 'measured') return 4;
                return 3;
            };
            return rank(left) - rank(right)
                || Number(left.purchasedNumber) - Number(right.purchasedNumber);
        })
    ), [linkedRows]);

    const pricingRows = useMemo(() => (
        linkedRows.filter((row) => row.existingItem && row.has3D)
    ), [linkedRows]);

    const issueRows = useMemo(() => (
        rows.filter((row) => (
            row.status === 'duplicate'
            || row.status === 'orphan-item'
            || row.geometryStatus === 'failed'
            || !row.has3D
        ))
    ), [rows]);

    const pendingGeometryRows = useMemo(() => (
        geometryRows.filter((row) => (
            row.product?._id
            && row.product?.modelPath
            && row.existingItem
            && !['measured', 'failed', 'unavailable'].includes(row.geometryStatus)
        ))
    ), [geometryRows]);

    const failedGeometryRows = useMemo(() => (
        geometryRows.filter((row) => (
            row.product?._id
            && row.product?.modelPath
            && row.existingItem
            && row.geometryStatus === 'failed'
        ))
    ), [geometryRows]);

    const measuredGeometryRows = useMemo(() => (
        geometryRows.filter((row) => row.geometryStatus === 'measured')
    ), [geometryRows]);

    const without3DRows = useMemo(() => (
        geometryRows.filter((row) => !row.has3D)
    ), [geometryRows]);

    const uncheckedPricingRows = useMemo(() => (
        pricingRows.filter((row) => !isReviewedStatus(row.existingItem?.reviewStatus))
    ), [pricingRows]);

    const checkedPricingRows = useMemo(() => (
        pricingRows.filter((row) => isReviewedStatus(row.existingItem?.reviewStatus))
    ), [pricingRows]);

    // Сервер уже применил pricingFilter к текущей странице.
    // Повторная фильтрация по transfer скрывала строки, хотя pagination.total
    // корректно показывал найденные позиции.
    const notImportedPricingRows = useMemo(() => pricingRows, [pricingRows]);

    const importedPricingRows = useMemo(() => pricingRows, [pricingRows]);

    const visibleRows = useMemo(() => {
        if (activeSection === 'geometry') {
            if (geometryFilter === 'unchecked') return pendingGeometryRows;
            if (geometryFilter === 'measured') return measuredGeometryRows;
            if (geometryFilter === 'failed') return failedGeometryRows;
            if (geometryFilter === 'without3D') return without3DRows;
            return geometryRows;
        }

        if (activeSection === 'pricing') {
            if (pricingFilter === 'unchecked') return uncheckedPricingRows;
            if (pricingFilter === 'checked') return checkedPricingRows;
            if (pricingFilter === 'notImported') return notImportedPricingRows;
            if (pricingFilter === 'imported') return importedPricingRows;
            return pricingRows;
        }

        if (activeSection === 'issues') return issueRows;
        return catalogRows;
    }, [
        activeSection,
        geometryFilter,
        pricingFilter,
        catalogRows,
        geometryRows,
        pendingGeometryRows,
        measuredGeometryRows,
        failedGeometryRows,
        without3DRows,
        pricingRows,
        uncheckedPricingRows,
        checkedPricingRows,
        notImportedPricingRows,
        importedPricingRows,
        issueRows,
    ]);

    const stopBatch = useCallback(() => {
        cancelBatchRef.current = true;
    }, []);

    const run3DBatch = useCallback(async (sourceRows, mode = 'pending') => {
        const queue = Array.isArray(sourceRows) ? [...sourceRows] : [];

        if (!queue.length) {
            showMessage(
                'success',
                mode === 'failed'
                    ? 'Ошибок для повторной проверки нет.'
                    : 'Для всех доступных 3D-моделей размер уже получен.'
            );
            return;
        }

        const realtimeKey = operationKey('geometry');
        cancelBatchRef.current = false;
        lastBatchOperationAtRef.current = 0;
        setMessage(null);
        setBatch({
            running: true,
            current: 0,
            total: queue.length,
            success: 0,
            failed: 0,
            currentNumber: null,
        });
        onRealtimeOperation?.({
            key: realtimeKey,
            running: true,
            current: 0,
            total: queue.length,
            success: 0,
            failed: 0,
            currentNumber: null,
            progress: 0,
        });

        let success = 0;
        let failed = 0;

        try {
            for (let index = 0; index < queue.length; index += 1) {
                if (cancelBatchRef.current) break;

                const row = queue[index];
                const product = row?.product;

                setBatch((previous) => ({
                    ...previous,
                    current: index + 1,
                    currentNumber: row?.purchasedNumber || null,
                    success,
                    failed,
                }));

                const operationNow = Date.now();
                if (
                    operationNow - lastBatchOperationAtRef.current >= 250
                    || index === queue.length - 1
                ) {
                    lastBatchOperationAtRef.current = operationNow;
                    onRealtimeOperation?.({
                        key: realtimeKey,
                        running: true,
                        current: index + 1,
                        total: queue.length,
                        success,
                        failed,
                        currentNumber: row?.purchasedNumber || null,
                        progress: ((index + 1) / Math.max(1, queue.length)) * 100,
                    });
                }

                if (!row?.purchasedNumber || !product?._id) {
                    failed += 1;
                    continue;
                }

                let loadedModelData = null;

                try {
                    const action = await dispatch(
                        getAdminProductModelData(product, { cache: false })
                    );
                    const modelData = action?.payload;
                    loadedModelData = modelData;

                    const measurement = measureCatalogModelGeometry({
                        modelData,
                        productDimensions: product?.dimensions || {},
                    });

                    await saveCatalogExtensionGeometry({
                        purchasedNumber: row.purchasedNumber,
                        productId: product._id,
                        measurement,
                        applyToPricing: true,
                    });

                    success += 1;
                } catch (error) {
                    failed += 1;

                    const errorCode = getErrorCode(error);
                    const rawText = getErrorMessage(error);
                    const errorText = errorCode === 'PRODUCT_MODEL_FILE_NOT_FOUND'
                        ? 'В карточке указан modelPath, но файл 3D-модели отсутствует на сервере'
                        : (
                            errorCode === 'PRODUCT_MODEL_NOT_CONFIGURED'
                                ? 'У товара не указана 3D-модель'
                                : rawText
                        );

                    console.warn(
                        `[CatalogReview][3D][${row.purchasedNumber}]`,
                        errorCode || 'MODEL_ANALYSIS_ERROR',
                        errorText
                    );

                    try {
                        await saveCatalogExtensionGeometry({
                            purchasedNumber: row.purchasedNumber,
                            productId: product._id,
                            measurement: {
                                status: 'failed',
                                productDimensions: product?.dimensions || {},
                                warnings: [
                                    errorCode
                                        ? `${errorCode}: ${errorText}`
                                        : errorText,
                                ],
                            },
                            applyToPricing: false,
                        });
                    } catch (saveError) {
                        console.warn(
                            `[CatalogReview][3D][${row.purchasedNumber}][save]`,
                            getErrorMessage(saveError)
                        );
                    }
                } finally {
                    disposeCatalogModelData(loadedModelData);
                }

                setBatch((previous) => ({
                    ...previous,
                    success,
                    failed,
                }));

                await new Promise((resolve) => window.setTimeout(resolve, 35));
            }
        } finally {
            setBatch((previous) => ({
                ...previous,
                running: false,
                currentNumber: null,
                success,
                failed,
            }));

            showMessage(
                failed ? 'warning' : 'success',
                cancelBatchRef.current
                    ? `Анализ остановлен. Получено размеров: ${success}, ошибок: ${failed}.`
                    : `Анализ завершён. Получено размеров: ${success}, ошибок: ${failed}.`
            );

            await loadWorkspace({ silent: true });
            await onRefresh?.();
            await onRealtimeChange?.({
                kind: 'geometry-analysis',
                categoryId,
            });
            onRealtimeOperation?.({
                key: realtimeKey,
                running: false,
                current: queue.length,
                total: queue.length,
                success,
                failed,
                progress: 100,
            });
        }
    }, [
        categoryId,
        dispatch,
        loadWorkspace,
        onRealtimeChange,
        onRealtimeOperation,
        onRefresh,
        operationKey,
        showMessage,
    ]);

    const analyze3D = useCallback(async () => {
        setActionLoading('prepare-geometry');
        setMessage(null);

        try {
            const queue = await loadAllRowsForAction({
                section: 'geometry',
                geometryFilter: 'unchecked',
            });
            setActionLoading('');
            return run3DBatch(queue, 'pending');
        } catch (error) {
            showMessage('error', getErrorMessage(error));
            return null;
        } finally {
            setActionLoading('');
        }
    }, [loadAllRowsForAction, run3DBatch, showMessage]);

    const retryFailed3D = useCallback(async () => {
        setActionLoading('prepare-geometry');
        setMessage(null);

        try {
            const queue = await loadAllRowsForAction({
                section: 'geometry',
                geometryFilter: 'failed',
            });
            setActionLoading('');
            return run3DBatch(queue, 'failed');
        } catch (error) {
            showMessage('error', getErrorMessage(error));
            return null;
        } finally {
            setActionLoading('');
        }
    }, [loadAllRowsForAction, run3DBatch, showMessage]);

    const recalculatePrices = useCallback(async () => {
        const realtimeKey = operationKey('estimate');
        setActionLoading('estimate');
        setMessage(null);
        onRealtimeOperation?.({ key: realtimeKey, running: true });

        try {
            const result = await estimateCatalogExtension(payload);
            showMessage(
                'success',
                result.message || `Рассчитано цен: ${result.estimatedCount}`
            );
            await loadWorkspace({ silent: true });
            await onRefresh?.();
            await onRealtimeChange?.({
                kind: 'price-estimate',
                categoryId,
            });
        } catch (error) {
            showMessage('error', getErrorMessage(error));
        } finally {
            setActionLoading('');
            onRealtimeOperation?.({ key: realtimeKey, running: false });
        }
    }, [
        categoryId,
        payload,
        onRealtimeOperation,
        operationKey,
        loadWorkspace,
        onRealtimeChange,
        onRefresh,
        showMessage,
    ]);

    const applyReadyPrices = useCallback(async () => {
        setActionLoading('prepare-apply');
        setMessage(null);

        let allReadyRows;
        try {
            allReadyRows = await loadAllRowsForAction({
                section: 'pricing',
                pricingFilter: 'notImported',
            });
        } catch (error) {
            showMessage('error', getErrorMessage(error));
            setActionLoading('');
            return;
        }

        // pricingFilter=notImported уже отфильтрован backend.
        // В товар переносим подтверждённый базовый размер формы и цену
        // фрезеровки; backend сам синхронизирует v11 shared sizes/варианты.
        const mappings = allReadyRows
            .filter((row) => (
                row.product?._id
                && row.existingItem?.readyForAdminImport === true
            ))
            .map((row) => ({
                productId: row.product._id,
                purchasedNumber: row.purchasedNumber,
            }));

        if (!mappings.length) {
            showMessage(
                'warning',
                'Нет подтверждённых размеров и цен для переноса в товары.'
            );
            setActionLoading('');
            return;
        }

        if (!window.confirm(
            `Перенести базовый размер и цену фрезеровки в ${mappings.length} товаров?`
        )) {
            setActionLoading('');
            return;
        }

        const realtimeKey = operationKey('apply');
        setActionLoading('apply');
        setMessage(null);
        onRealtimeOperation?.({ key: realtimeKey, running: true });

        try {
            const result = await applyCatalogProductLinks(mappings);
            const rows = Array.isArray(result?.results)
                ? result.results
                : [];
            const failedRows = rows.filter(
                (row) => row?.ok !== true && row?.skipped !== true
            );
            const skippedRows = rows.filter((row) => row?.skipped === true);
            const problemRows = [...failedRows, ...skippedRows];
            const details = problemRows
                .slice(0, 4)
                .map((row) => (
                    `№${row.purchasedNumber || '—'}: ${row.error || 'ошибка переноса'}`
                ))
                .join(' · ');
            const messageText = [
                result?.message || 'Перенос завершён.',
                details,
                problemRows.length > 4
                    ? `ещё сообщений: ${problemRows.length - 4}`
                    : '',
            ].filter(Boolean).join(' · ');

            showMessage(
                failedRows.length || skippedRows.length
                    ? 'warning'
                    : 'success',
                messageText
            );

            await loadWorkspace({ silent: true });
            await onRefresh?.();

            const appliedNumbers = rows.length
                ? rows
                    .filter((row) => row?.ok === true)
                    .map((row) => Number(row.purchasedNumber || 0))
                    .filter(Boolean)
                : mappings.map((row) => row.purchasedNumber);

            if (appliedNumbers.length) {
                await onRealtimeChange?.({
                    kind: 'prices-applied',
                    categoryId,
                    purchasedNumbers: appliedNumbers,
                });
            }
        } catch (error) {
            showMessage('error', getErrorMessage(error));
        } finally {
            setActionLoading('');
            onRealtimeOperation?.({ key: realtimeKey, running: false });
        }
    }, [
        categoryId,
        loadAllRowsForAction,
        onRealtimeOperation,
        operationKey,
        loadWorkspace,
        onRealtimeChange,
        onRefresh,
        showMessage,
    ]);

    const applySinglePrice = useCallback(async (row) => {
        const purchasedNumber = Number(row?.purchasedNumber || 0);
        const productId = String(row?.product?._id || '');
        const readyForTransfer = row?.existingItem?.readyForAdminImport === true;

        if (!purchasedNumber || !productId || !readyForTransfer) {
            showMessage('warning', 'Эта строка сейчас не находится в очереди переноса.');
            return;
        }

        const realtimeKey = operationKey('apply');
        setSingleTransferNumber(purchasedNumber);
        setMessage(null);
        onRealtimeOperation?.({ key: realtimeKey, running: true });

        try {
            const result = await applyCatalogProductLinks([{
                productId,
                purchasedNumber,
            }]);
            const resultRow = Array.isArray(result?.results)
                ? result.results[0]
                : null;

            if (resultRow?.ok === true) {
                showMessage(
                    'success',
                    `Форма №${purchasedNumber} перенесена в товар.`
                );
            } else {
                showMessage(
                    resultRow?.skipped === true ? 'warning' : 'error',
                    `№${purchasedNumber}: ${resultRow?.error || result?.message || 'Не удалось перенести'}`
                );
            }

            await loadWorkspace({ silent: true });
            await onRefresh?.();

            if (resultRow?.ok === true) {
                await onRealtimeChange?.({
                    kind: 'prices-applied',
                    categoryId,
                    purchasedNumbers: [purchasedNumber],
                });
            }
        } catch (error) {
            showMessage('error', getErrorMessage(error));
        } finally {
            setSingleTransferNumber(null);
            onRealtimeOperation?.({ key: realtimeKey, running: false });
        }
    }, [
        categoryId,
        loadWorkspace,
        onRealtimeChange,
        onRealtimeOperation,
        onRefresh,
        operationKey,
        showMessage,
    ]);

    const revertSinglePrice = useCallback(async (row) => {
        const purchasedNumber = Number(row?.purchasedNumber || 0);
        const productId = String(row?.product?._id || '');
        const imported = row?.transfer?.status === 'imported';

        if (!purchasedNumber || !productId || !imported) {
            showMessage('warning', 'Для этой строки сейчас нечего возвращать назад.');
            return;
        }

        if (!window.confirm(
            `Убрать перенесённую цену фрезеровки из товара для формы №${purchasedNumber}? `
            + 'Проверенная цена останется в каталоге и снова появится в очереди переноса.'
        )) {
            return;
        }

        const realtimeKey = operationKey('apply');
        setSingleRevertNumber(purchasedNumber);
        setMessage(null);
        onRealtimeOperation?.({ key: realtimeKey, running: true });

        try {
            const result = await revertCatalogProductLinks([{
                productId,
                purchasedNumber,
            }]);
            const resultRow = Array.isArray(result?.results)
                ? result.results[0]
                : null;

            if (resultRow?.ok === true) {
                showMessage(
                    'success',
                    `Форма №${purchasedNumber}: перенос отменён, цена снова готова к переносу.`
                );
            } else {
                showMessage(
                    resultRow?.skipped === true ? 'warning' : 'error',
                    `№${purchasedNumber}: ${resultRow?.error || result?.message || 'Не удалось вернуть цену'}`
                );
            }

            await loadWorkspace({ silent: true });
            await onRefresh?.();

            if (resultRow?.ok === true) {
                await onRealtimeChange?.({
                    kind: 'prices-reverted',
                    categoryId,
                    purchasedNumbers: [purchasedNumber],
                });
            }
        } catch (error) {
            showMessage('error', getErrorMessage(error));
        } finally {
            setSingleRevertNumber(null);
            onRealtimeOperation?.({ key: realtimeKey, running: false });
        }
    }, [
        categoryId,
        loadWorkspace,
        onRealtimeChange,
        onRealtimeOperation,
        onRefresh,
        operationKey,
        showMessage,
    ]);

    const downloadReport = useCallback(async () => {
        const realtimeKey = operationKey('report');
        setActionLoading('report');
        setMessage(null);
        onRealtimeOperation?.({ key: realtimeKey, running: true });

        try {
            const blob = await downloadCatalogExtensionReport(payload);
            const url = window.URL.createObjectURL(blob);
            const anchor = document.createElement('a');

            anchor.href = url;
            anchor.download = `catalog-products-${CATALOG_FROM}-${CATALOG_TO}.csv`;
            document.body.appendChild(anchor);
            anchor.click();
            anchor.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            showMessage('error', getErrorMessage(error));
        } finally {
            setActionLoading('');
            onRealtimeOperation?.({ key: realtimeKey, running: false });
        }
    }, [payload, showMessage, onRealtimeOperation, operationKey]);

    const summary = workspace?.summary || {};
    const summaryGroups = [
        {
            key: 'catalog',
            label: 'Каталог',
            value: `${summary.productsScanned || 0} товаров`,
            details: [
                `${summary.foundNumbers || 0} с номером`,
                `${summary.linkedItems || 0} связано`,
            ],
        },
        {
            key: 'geometry',
            label: '3D и размеры',
            value: `${summary.with3D || 0} моделей`,
            details: [
                `${summary.geometryMeasured || 0} измерено`,
                `${Number(summary.geometryPending || 0)} ожидают`,
            ],
        },
        {
            key: 'pricing',
            label: 'Цены',
            value: `${summary.priceEstimated || 0} заполнено`,
            details: [
                `${summary.readyForImport || 0} к переносу`,
                `${summary.importedCount || 0} перенесено`,
            ],
        },
        {
            key: 'issues',
            label: 'Проблемы',
            value: String(summary.problemCount || 0),
            details: [
                `${summary.withoutNumber || 0} без номера`,
                `${summary.duplicateNumbers || 0} дублей`,
            ],
        },
    ];

    const selectSection = (section) => {
        if (section === activeSection) return;
        setWorkspaceLoading(true);
        setPageNumber(0);
        setActiveSection(section);
        onDraftChange?.({
            catalogWorkspaceTab: section,
            catalogPageNumber: 0,
        });
    };

    const selectGeometryFilter = (filter) => {
        if (activeSection === 'geometry' && geometryFilter === filter) return;
        setWorkspaceLoading(true);
        setPageNumber(0);
        setGeometryFilter(filter);
        setActiveSection('geometry');
        onDraftChange?.({
            catalogWorkspaceTab: 'geometry',
            catalogGeometryFilter: filter,
            catalogPageNumber: 0,
        });
    };

    const selectPricingFilter = (filter) => {
        if (activeSection === 'pricing' && pricingFilter === filter) return;
        setWorkspaceLoading(true);
        setPageNumber(0);
        setPricingFilter(filter);
        setActiveSection('pricing');
        onDraftChange?.({
            catalogWorkspaceTab: 'pricing',
            catalogPricingFilter: filter,
            catalogPageNumber: 0,
        });
    };

    const changePageNumber = (nextPage) => {
        const normalizedPage = Math.max(
            0,
            Math.floor(Number(nextPage) || 0)
        );

        if (normalizedPage === Number(pageNumber)) return;

        setWorkspaceLoading(true);
        setPageNumber(normalizedPage);
        onDraftChange?.({
            catalogPageNumber: normalizedPage,
        });
    };

    const startUncheckedPricingReview = async () => {
        setActionLoading('prepare-review');
        setMessage(null);

        let allUncheckedRows;
        try {
            allUncheckedRows = await loadAllRowsForAction({
                section: 'pricing',
                pricingFilter: 'unchecked',
            });
        } catch (error) {
            showMessage('error', getErrorMessage(error));
            setActionLoading('');
            return;
        }

        const first = allUncheckedRows[0];
        if (!first) {
            showMessage('success', 'Непроверенных цен больше нет.');
            setActionLoading('');
            return;
        }

        setActionLoading('');
        onOpenPricing?.(first.purchasedNumber, {
            quickQueue: true,
            queueNumbers: allUncheckedRows.map((row) => row.purchasedNumber),
        });
    };

    const remoteAction = remoteAnalyze
        ? 'analyze'
        : (remoteEstimate
            ? 'estimate'
            : (remoteApply ? 'apply' : (remoteReport ? 'report' : '')));
    const visibleAction = actionLoading === 'prepare-review'
        ? remoteAction
        : (actionLoading || remoteAction);
    const actionLabels = {
        analyze: 'Анализируем каталог товаров…',
        'prepare-geometry': 'Подготавливаем очередь 3D-моделей…',
        estimate: 'Рассчитываем отсутствующие цены…',
        'prepare-review': 'Собираем очередь непроверенных цен…',
        'prepare-apply': 'Подготавливаем товары для переноса…',
        apply: 'Переносим подтверждённые цены в товары…',
        report: 'Формируем CSV-отчёт…',
    };
    const actionDescription = visibleAction === 'analyze'
        && effectiveCategoryProgress.total > 0
        ? [
            `${effectiveCategoryProgress.current} из ${effectiveCategoryProgress.total}`,
            effectiveCategoryProgress.name,
        ].filter(Boolean).join(' · ')
        : '';

    return (
        <section
            className="catalogReview__panel catalogReview__extensionPanel catalogReview__unifiedCatalog"
            data-catalog-sync-scope={`extension-${categoryId || 'none'}`}
        >
            <div className="catalogReview__extensionTopbar">
                <div>
                    <span>Номера №{CATALOG_FROM}–{CATALOG_TO}</span>
                    <small>
                        Номер берётся из <code>description</code>. Анализ не меняет
                        поля товара, пока ты явно не перенесёшь подтверждённые цены.
                    </small>
                </div>

                <button
                    type="button"
                    className="is-primary"
                    disabled={effectiveLoading || effectiveBatch.running || !categoryId}
                    onClick={analyzeAll}
                >
                    {analyzeRunning ? (
                        <span className="catalogReview__buttonLoading">
                            <i className="catalogReview__buttonSpinner" aria-hidden="true" />
                            {effectiveCategoryProgress.total > 0
                                ? `Анализ ${effectiveCategoryProgress.current}/${effectiveCategoryProgress.total}`
                                : 'Анализ…'}
                        </span>
                    ) : 'Проанализировать заново'}
                </button>
            </div>

            {message ? (
                <div className={`catalogReview__message catalogReview__message--${message.type}`}>
                    {message.text}
                </div>
            ) : null}

            {visibleAction ? (
                <CatalogLoadingState
                    compact
                    title={actionLabels[visibleAction] || 'Выполняем операцию…'}
                    description={actionDescription}
                />
            ) : null}

            {!workspace && workspaceLoading ? (
                <CatalogLoadingState
                    title="Загружаем товары, 3D и цены…"
                    description="Подготавливаем сводку и первые строки выбранной категории."
                />
            ) : workspace ? (
                <div className="catalogReview__extensionWorkspace">
                    <div
                        className="catalogReview__extensionStats catalogReview__unifiedStats"
                        aria-label="Сводка выбранной категории"
                    >
                        {summaryGroups.map((group) => (
                            <article
                                key={group.key}
                                className={`catalogReview__summaryGroup is-${group.key}`}
                            >
                                <span>{group.label}</span>
                                <strong>{group.value}</strong>
                                <small>{group.details.join(' · ')}</small>
                            </article>
                        ))}
                    </div>

                    <AdminSearchFilterPanel
                        title="Поиск по каталогу"
                        summary={catalogSearch
                            ? `Найденное по запросу: ${catalogSearch}`
                            : 'Номер формы, название товара, артикул, категория или тег'}
                        open={catalogSearchPanelOpen}
                        onOpenChange={changeCatalogSearchPanelOpen}
                        searchValue={catalogSearchDraft}
                        onSearchChange={changeCatalogSearchDraft}
                        searchLabel="Поиск"
                        searchPlaceholder="№ формы, название, артикул, категория, тег"
                        disabled={workspaceLoading || !categoryId}
                    />

                    <div className="catalogReview__catalogSections">
                        {SECTIONS.map((section) => (
                            <button
                                type="button"
                                key={section.value}
                                className={activeSection === section.value ? 'is-active' : ''}
                                onClick={() => selectSection(section.value)}
                            >
                                {workspaceLoading && activeSection === section.value ? (
                                    <i className="catalogReview__buttonSpinner" aria-hidden="true" />
                                ) : null}
                                {section.label}
                            </button>
                        ))}
                    </div>

                    <div className="catalogReview__extensionActions catalogReview__contextActions">
                        {activeSection === 'catalog' ? (
                            <span>
                                {summary.foundNumbers || 0} с номером
                                {' · '}{summary.withoutNumber || 0} без номера
                                {' · '}{summary.duplicateNumbers || 0} дублей
                            </span>
                        ) : null}

                        {activeSection === 'geometry' ? (
                            <>
                                <div className="catalogReview__primaryActions">
                                    {!effectiveBatch.running ? (
                                        <>
                                            <button
                                                type="button"
                                                className="is-primary"
                                                disabled={!Number(summary.geometryPending || 0) || effectiveLoading}
                                                onClick={analyze3D}
                                            >
                                                {geometryQueueLoading ? (
                                                    <span className="catalogReview__buttonLoading">
                                                        <i className="catalogReview__buttonSpinner" aria-hidden="true" />
                                                        Подготовка…
                                                    </span>
                                                ) : `Получить размеры · ${Number(summary.geometryPending || 0)}` }
                                            </button>
                                            {Number(summary.geometryFailed || 0) ? (
                                                <button
                                                    type="button"
                                                    className="is-warning"
                                                    disabled={effectiveLoading}
                                                    onClick={retryFailed3D}
                                                >
                                                    {geometryQueueLoading ? (
                                                        <span className="catalogReview__buttonLoading">
                                                            <i className="catalogReview__buttonSpinner" aria-hidden="true" />
                                                            Подготовка…
                                                        </span>
                                                    ) : `Повторить ошибки · ${Number(summary.geometryFailed || 0)}` }
                                                </button>
                                            ) : null}
                                        </>
                                    ) : (
                                        <button
                                            type="button"
                                            className="is-danger"
                                            onClick={stopBatch}
                                        >
                                            Остановить после текущей модели
                                        </button>
                                    )}
                                </div>
                                <div className="catalogReview__reviewFilterBar">
                                    <button
                                        type="button"
                                        className={geometryFilter === 'unchecked' ? 'is-active is-unchecked' : ''}
                                        onClick={() => selectGeometryFilter('unchecked')}
                                    >
                                        Ожидают {Number(summary.geometryPending || 0)}
                                    </button>
                                    <button
                                        type="button"
                                        className={geometryFilter === 'measured' ? 'is-active is-checked' : ''}
                                        onClick={() => selectGeometryFilter('measured')}
                                    >
                                        Измерены {Number(summary.geometryMeasured || 0)}
                                    </button>
                                    <button
                                        type="button"
                                        className={geometryFilter === 'failed' ? 'is-active is-error' : ''}
                                        onClick={() => selectGeometryFilter('failed')}
                                    >
                                        Ошибки {Number(summary.geometryFailed || 0)}
                                    </button>
                                    <button
                                        type="button"
                                        className={geometryFilter === 'without3D' ? 'is-active' : ''}
                                        onClick={() => selectGeometryFilter('without3D')}
                                    >
                                        Без 3D {Number(summary.without3D || 0)}
                                    </button>
                                    <button
                                        type="button"
                                        className={geometryFilter === 'all' ? 'is-active' : ''}
                                        onClick={() => selectGeometryFilter('all')}
                                    >
                                        Все {Number(summary.linkedItems || 0)}
                                    </button>
                                </div>
                            </>
                        ) : null}

                        {activeSection === 'pricing' ? (
                            <>
                                <div className="catalogReview__primaryActions">
                                    <button
                                        type="button"
                                        className="is-primary"
                                        disabled={effectiveLoading || effectiveBatch.running}
                                        onClick={recalculatePrices}
                                    >
                                        {estimateRunning ? (
                                            <span className="catalogReview__buttonLoading">
                                                <i className="catalogReview__buttonSpinner" aria-hidden="true" />
                                                Рассчитываем…
                                            </span>
                                        ) : 'Рассчитать отсутствующие цены'}
                                    </button>
                                    <button
                                        type="button"
                                        className="is-review-start"
                                        disabled={!Number(summary.pricingUnchecked || 0) || effectiveLoading || effectiveBatch.running}
                                        onClick={startUncheckedPricingReview}
                                    >
                                        {reviewQueueLoading ? (
                                            <span className="catalogReview__buttonLoading">
                                                <i className="catalogReview__buttonSpinner" aria-hidden="true" />
                                                Подготовка…
                                            </span>
                                        ) : `Начать проверку · ${Number(summary.pricingUnchecked || 0)}` }
                                    </button>
                                    <button
                                        type="button"
                                        className="is-success"
                                        disabled={effectiveLoading || effectiveBatch.running || !summary.readyForImport}
                                        onClick={applyReadyPrices}
                                    >
                                        {applyRunning ? (
                                            <span className="catalogReview__buttonLoading">
                                                <i className="catalogReview__buttonSpinner" aria-hidden="true" />
                                                {actionLoading === 'prepare-apply' ? 'Подготовка…' : 'Переносим…'}
                                            </span>
                                        ) : `Перенести все · ${summary.readyForImport || 0}` }
                                    </button>
                                </div>
                                <div
                                    className="catalogReview__reviewFilterBar"
                                    data-catalog-sync-scope="catalog-pricing-filters"
                                    aria-label="Фильтры цен и переноса"
                                >
                                    <button
                                        type="button"
                                        aria-pressed={pricingFilter === 'notImported'}
                                        className={pricingFilter === 'notImported'
                                            ? 'is-active is-not-imported'
                                            : ''}
                                        onClick={() => selectPricingFilter('notImported')}
                                        title="Подтверждены, но ещё не перенесены или изменены после переноса"
                                    >
                                        Не перенесены {Number(summary.readyForImport || 0)}
                                    </button>
                                    <button
                                        type="button"
                                        aria-pressed={pricingFilter === 'imported'}
                                        className={pricingFilter === 'imported'
                                            ? 'is-active is-imported'
                                            : ''}
                                        onClick={() => selectPricingFilter('imported')}
                                        title="Фрезеровка уже перенесена в товар и после этого не изменялась"
                                    >
                                        Перенесены {Number(summary.importedCount || 0)}
                                    </button>
                                    <button
                                        type="button"
                                        aria-pressed={pricingFilter === 'unchecked'}
                                        className={pricingFilter === 'unchecked' ? 'is-active is-unchecked' : ''}
                                        onClick={() => selectPricingFilter('unchecked')}
                                    >
                                        Не проверены {Number(summary.pricingUnchecked || 0)}
                                    </button>
                                    <button
                                        type="button"
                                        aria-pressed={pricingFilter === 'checked'}
                                        className={pricingFilter === 'checked' ? 'is-active is-checked' : ''}
                                        onClick={() => selectPricingFilter('checked')}
                                    >
                                        Проверены {Number(summary.pricingChecked || 0)}
                                    </button>
                                    <button
                                        type="button"
                                        aria-pressed={pricingFilter === 'all'}
                                        className={pricingFilter === 'all' ? 'is-active' : ''}
                                        onClick={() => selectPricingFilter('all')}
                                    >
                                        Все {Number(summary.pricingUnchecked || 0) + Number(summary.pricingChecked || 0)}
                                    </button>
                                </div>
                            </>
                        ) : null}

                        {activeSection === 'issues' ? (
                            <>
                                <div className="catalogReview__primaryActions">
                                    <button
                                        type="button"
                                        disabled={effectiveLoading || effectiveBatch.running}
                                        onClick={downloadReport}
                                    >
                                        {reportRunning ? (
                                            <span className="catalogReview__buttonLoading">
                                                <i className="catalogReview__buttonSpinner" aria-hidden="true" />
                                                Формируем…
                                            </span>
                                        ) : 'Скачать CSV'}
                                    </button>
                                </div>
                                <span>
                                    Здесь собраны дубли, товары без номера,
                                    модели без 3D и ошибки измерения.
                                </span>
                            </>
                        ) : null}
                    </div>

                    {effectiveBatch.running || effectiveBatch.total > 0 ? (
                        <div className="catalogReview__extensionProgress">
                            <div>
                                <strong>
                                    {effectiveBatch.running
                                        ? `Анализ формы №${effectiveBatch.currentNumber || '—'}`
                                        : 'Последний анализ завершён'}
                                </strong>
                                <span>
                                    {effectiveBatch.current} / {effectiveBatch.total}
                                    {' · '}успешно {effectiveBatch.success}
                                    {' · '}ошибок {effectiveBatch.failed}
                                </span>
                            </div>
                            <progress
                                max={Math.max(1, effectiveBatch.total)}
                                value={Math.min(effectiveBatch.current, effectiveBatch.total)}
                            />
                        </div>
                    ) : null}

                    <div
                        className="catalogReview__extensionTableWrap"
                        data-catalog-scroll-id="catalog-products-table"
                    >
                        <table className="catalogReview__extensionTable">
                            <thead>
                                <tr>
                                    <th>Форма</th>
                                    <th>Фото</th>
                                    <th>Товар</th>
                                    <th>3D и размер</th>
                                    <th>Фрезеровка</th>
                                    <th>Состояние</th>
                                    <th>Действия</th>
                                </tr>
                            </thead>
                            <tbody>
                                {visibleRows.length ? visibleRows.map((row) => {
                                    const item = row.existingItem || {};
                                    const geometry = item.geometryAnalysis || {};
                                    const dimensions = geometry.status === 'measured'
                                        ? geometry.dimensions
                                        : null;
                                    const dimensionText = hasDimensions(dimensions)
                                        ? size(dimensions)
                                        : (
                                            row.has3D
                                                ? 'Нужно получить размер 3D'
                                                : 'Нужна 3D-модель'
                                        );
                                    const estimate = item.estimate || {};
                                    const price = item.pricingDraft?.basePrice || 0;
                                    const imageCount = Array.from(new Set([
                                        row.imagePath,
                                        ...(row.galleryImages || []),
                                        ...(row.product?.galleryImages || []),
                                    ].filter(Boolean))).length;

                                    const reviewed = isReviewedStatus(item.reviewStatus);
                                    const rowClassName = [
                                        reviewed ? 'is-review-checked' : 'is-review-unchecked',
                                        row.geometryStatus ? `is-geometry-${row.geometryStatus}` : '',
                                        row.status === 'duplicate' ? 'is-row-duplicate' : '',
                                    ].filter(Boolean).join(' ');

                                    return (
                                        <tr
                                            key={`${activeSection}-${row._scopeKey || row.purchasedNumber}`}
                                            className={rowClassName}
                                            data-catalog-sync-scope={`catalog-row-${row._scopeKey || row.purchasedNumber}`}
                                        >
                                            <td>
                                                <strong>№{row.purchasedNumber}</strong>
                                                {isAllCategories ? (
                                                    <small className="catalogReview__rowCategory">
                                                        {row._scopeCategoryName || 'Категория'}
                                                    </small>
                                                ) : null}
                                                {item.catalogSource?.type === 'new-catalog-product' ? (
                                                    <span className="catalogReview__extensionSource">
                                                        Новый каталог
                                                    </span>
                                                ) : null}
                                            </td>
                                            <td>
                                                <button
                                                    type="button"
                                                    className="catalogReview__extensionImageButton"
                                                    disabled={!row.imagePath}
                                                    onClick={() => openGallery(row)}
                                                >
                                                    {row.imagePath ? (
                                                        <img
                                                            src={resolveAnyStaticUrl(row.imagePath)}
                                                            alt={`Форма №${row.purchasedNumber}`}
                                                            loading="lazy"
                                                        />
                                                    ) : (
                                                        <span>Нет фото</span>
                                                    )}
                                                    <small>{imageCount} фото</small>
                                                </button>
                                            </td>
                                            <td>
                                                {row.status === 'duplicate' ? (
                                                    <>
                                                        <strong>
                                                            {row.products?.length || 0} товара
                                                        </strong>
                                                        {(row.products || [])
                                                            .filter(Boolean)
                                                            .slice(0, 3)
                                                            .map((product, index) => (
                                                                <small key={product._id || index}>
                                                                    {productName(product)}
                                                                </small>
                                                            ))}
                                                    </>
                                                ) : (
                                                    <>
                                                        <strong>{productName(row.product)}</strong>
                                                        <small>
                                                            Артикул: {row.product?.article || '—'}
                                                        </small>
                                                        <small>
                                                            Номер: description
                                                        </small>
                                                    </>
                                                )}
                                            </td>
                                            <td>
                                                <strong>{dimensionText}</strong>
                                                <span className={row.has3D ? 'is-ok' : 'is-muted'}>
                                                    {row.has3D ? '3D есть' : '3D нет'}
                                                </span>
                                                <small>
                                                    {geometryStatusLabel(row.geometryStatus)}
                                                </small>
                                            </td>
                                            <td>
                                                <strong>{money(price)}</strong>
                                                <small>
                                                    {money(estimate.priceRange?.min)}
                                                    {' — '}
                                                    {money(estimate.priceRange?.max)}
                                                </small>
                                                <span className={`catalogReview__reviewStatusPill ${
                                                    reviewed ? 'is-checked' : 'is-unchecked'
                                                }`}>
                                                    {reviewStatusLabel(item.reviewStatus)}
                                                </span>
                                                {getLastPriceEditFromItem(item)?.userName ? (
                                                    <small className="catalogReview__priceEditor">
                                                        Менял: <b>{getLastPriceEditFromItem(item).userName}</b>
                                                        {formatPriceEditTime(getLastPriceEditFromItem(item).createdAt)
                                                            ? ` · ${formatPriceEditTime(getLastPriceEditFromItem(item).createdAt)}`
                                                            : ''}
                                                    </small>
                                                ) : null}
                                            </td>
                                            <td>
                                                <strong className={`is-${row.status}`}>
                                                    {rowStateLabel(row)}
                                                </strong>
                                                {row.transfer?.status === 'imported' ? (
                                                    <small className="catalogReview__transferState is-imported">
                                                        Перенесено в товар
                                                    </small>
                                                ) : null}
                                                {row.transfer?.status === 'changed' ? (
                                                    <small className="catalogReview__transferState is-changed">
                                                        Есть изменения для переноса
                                                    </small>
                                                ) : null}
                                                {row.transfer?.status === 'pending' ? (
                                                    <small className="catalogReview__transferState is-pending">
                                                        Готово к переносу
                                                    </small>
                                                ) : null}
                                            </td>
                                            <td>
                                                <div className="catalogReview__extensionRowActions">
                                                    <button
                                                        type="button"
                                                        className="is-primary"
                                                        disabled={!item._id}
                                                        onClick={() => onOpenPricing?.(row.purchasedNumber, {
                                                            initialView: 'pricing',
                                                            queueNumbers: (
                                                                activeSection === 'pricing'
                                                                    ? visibleRows
                                                                    : pricingRows
                                                            )
                                                                .filter((candidate) => candidate.existingItem?._id)
                                                                .map((candidate) => candidate.purchasedNumber),
                                                        })}
                                                    >
                                                        Открыть карточку
                                                    </button>
                                                    {activeSection === 'pricing'
                                                        && item.readyForAdminImport === true
                                                        && row.product?._id ? (
                                                        <button
                                                            type="button"
                                                            className="is-success"
                                                            disabled={effectiveLoading || effectiveBatch.running}
                                                            onClick={() => applySinglePrice(row)}
                                                        >
                                                            {singleTransferNumber === row.purchasedNumber
                                                                ? 'Переносим…'
                                                                : 'Перенести'}
                                                        </button>
                                                    ) : null}
                                                    {activeSection === 'pricing'
                                                        && row.transfer?.status === 'imported'
                                                        && row.product?._id ? (
                                                        <button
                                                            type="button"
                                                            className="is-warning"
                                                            disabled={effectiveLoading || effectiveBatch.running}
                                                            onClick={() => revertSinglePrice(row)}
                                                        >
                                                            {singleRevertNumber === row.purchasedNumber
                                                                ? 'Возвращаем…'
                                                                : 'Вернуть назад'}
                                                        </button>
                                                    ) : null}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                }) : (
                                    <tr>
                                        <td colSpan="7">
                                            <div className="catalogReview__extensionEmptyRows">
                                                <strong>В этом разделе пока нет строк</strong>
                                                <span>Запрос завершён. Измени фильтр или выбери другой раздел.</span>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    <PaginationComponent
                        totalAll={Number(workspace.pagination?.total || 0)}
                        pageSize={pageSize}
                        pageNumber={pageNumber}
                        setPageNumber={changePageNumber}
                        totalPages={Math.max(1, Number(workspace.pagination?.totalPages || 1))}
                    />

                    {activeSection === 'issues' && workspace.withoutNumber?.length ? (
                        <details className="catalogReview__extensionMissing" open>
                            <summary>
                                Товары без номера в description: {workspace.withoutNumber.length}
                            </summary>
                            <div className="catalogReview__noNumberProducts">
                                {workspace.withoutNumber.map((product) => (
                                    <div key={product._scopeKey || product._id}>
                                        <strong>{productName(product)}</strong>
                                        {isAllCategories ? (
                                            <small className="catalogReview__rowCategory">
                                                {product._scopeCategoryName || 'Категория'}
                                            </small>
                                        ) : null}
                                        <span>{product.article || 'Без артикула'}</span>
                                        <code>{product.description || 'description пустой'}</code>
                                    </div>
                                ))}
                            </div>
                        </details>
                    ) : null}

                    {workspaceLoading ? (
                        <div className="catalogReview__extensionWorkspaceOverlay">
                            <CatalogLoadingState
                                compact
                                title="Обновляем список…"
                                description="Текущие строки остаются на экране до получения новых данных."
                            />
                        </div>
                    ) : null}
                </div>
            ) : (
                <div className="catalogReview__muted catalogReview__catalogStart">
                    Выбери категорию. Затем нажми «Проанализировать заново».
                    Система создаст недостающие записи каталога и свяжет их
                    с существующими товарами.
                </div>
            )}

            {galleryRow ? (
                <GalleryModal
                    row={galleryRow}
                    activeIndex={sharedDraft?.galleryImageIndex || 0}
                    onActiveIndexChange={(index) => onDraftChange?.({
                        galleryPurchasedNumber: galleryRow.purchasedNumber,
                        galleryScopeKey: galleryRow._scopeKey || '',
                        galleryImageIndex: index,
                    })}
                    onClose={closeGallery}
                />
            ) : null}
        </section>
    );
};

export default CatalogExtensionPanel;
